import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FACET_SECTION_FLASH_MS } from "../components/UIToolkit/Filters/FacetFilterSection";
import type { FacetSpec } from "../components/UIToolkit/Filters/facetSections";
import { applyFacetFilters, countActiveFacets, type FacetDimension, type FacetFilterResult, type FacetSelection } from "../utils/facetFilters";

const EMPTY_SELECTION: FacetSelection = {};

export type FacetFiltersState<T> = {
	selection: FacetSelection;
	result: FacetFilterResult<T>;
	toggle: (dimensionId: string, optionId: string, checked: boolean) => void;
	clear: () => void;
	activeCount: number;
	/** The group a column's "Filter by…" just pointed at. */
	highlightedId: string | null;
	highlight: (dimensionId: string) => void;
};

/** Client-side facet filtering for a grid. Keep `predicate` referentially stable. */
export function useFacetFilters<T>(rows: readonly T[], dimensions: readonly FacetDimension<T>[], predicate: (row: T) => boolean): FacetFiltersState<T> {
	const [selection, setSelection] = useState<FacetSelection>(EMPTY_SELECTION);
	const [highlightedId, setHighlightedId] = useState<string | null>(null);
	const highlightTimer = useRef<number | null>(null);

	const toggle = useCallback((dimensionId: string, optionId: string, checked: boolean) => {
		setSelection((prev) => {
			const next = new Set(prev[dimensionId]);
			if (checked) next.add(optionId);
			else next.delete(optionId);
			return { ...prev, [dimensionId]: next };
		});
	}, []);

	const clear = useCallback(() => setSelection(EMPTY_SELECTION), []);

	const highlight = useCallback((dimensionId: string) => {
		setHighlightedId(dimensionId);
		if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
		highlightTimer.current = window.setTimeout(() => setHighlightedId(null), FACET_SECTION_FLASH_MS);
	}, []);

	useEffect(
		() => () => {
			if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
		},
		[],
	);

	const result = useMemo(() => applyFacetFilters(rows, dimensions, selection, predicate), [rows, dimensions, selection, predicate]);
	const activeCount = useMemo(() => countActiveFacets(selection), [selection]);

	return { selection, result, toggle, clear, activeCount, highlightedId, highlight };
}

const keepEveryRow = (): boolean => true;

/** `useFacetFilters` for a grid narrowed only by its facet groups. */
export function useGridFacetFilters<T>(rows: readonly T[], facets: readonly FacetSpec<T>[]): FacetFiltersState<T> {
	const dimensions = useMemo(() => facets.map((facet) => facet.dimension), [facets]);
	return useFacetFilters(rows, dimensions, keepEveryRow);
}

export type FilterRailHandoff = {
	railCollapsed: boolean;
	setRailCollapsed: (collapsed: boolean) => void;
	canFilterByColumn: (field: string) => boolean;
	onFilterByColumn: (field: string) => void;
	filterByHint: (field: string) => string | undefined;
};

/** Rail collapse state plus a grid column menu's "Filter by…" hand-off: reopen the rail and flash the group. */
export function useFilterRailHandoff(
	highlight: (dimensionId: string) => void,
	columnFacet: Readonly<Partial<Record<string, string>>>,
	hint: string,
): FilterRailHandoff {
	const [railCollapsed, setRailCollapsed] = useState(false);
	const canFilterByColumn = useCallback((field: string) => columnFacet[field] != null, [columnFacet]);
	const onFilterByColumn = useCallback(
		(field: string) => {
			const facet = columnFacet[field];
			if (!facet) return;
			setRailCollapsed(false);
			highlight(facet);
		},
		[columnFacet, highlight],
	);
	const filterByHint = useCallback((field: string) => (columnFacet[field] ? hint : undefined), [columnFacet, hint]);
	return { railCollapsed, setRailCollapsed, canFilterByColumn, onFilterByColumn, filterByHint };
}
